/**
 * Main App component that handles auth state and routing.
 *
 * Initializes auth hook, fetches user account data, handles routing and navigation state.
 * Renders main app layout with Navigation, WorkspaceSelectModal and Content components.
 * Handles global state like selected workspace, environments, etc.
 * Redirects to Product page if no authenticated user.
 */
import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "./app/hooks/useAuth";
import { useLocalStorage } from "usehooks-ts";
import {Provider} from 'use-http';
import config from "./config";
import axios from "axios";
import qs from "qs";
import Navigation from "./app/components/Base/Layout/Navigation";
import WorkspaceSelectModal from "./app/components/Base/Elements/Nav/WorkspaceSelectModal";
import Content from "./app/components/Base/Layout/Content";
import Product from "./app/pages/Product";
import ChatwootWidget from './app/components/Base/Layout/ChatWootWidget';

export default function App() { 
  const history = useHistory();
  const auth = useAuth();
  const user = auth.user;  
  const [nav, setNav] = useState<any>(config.nav);
  const [account, setAccount] = useState<any>(user?.account);
  const [environments, setEnvironments] = useState<any>([]);
  const [selectedAccount, setSelectedAccount] = useState<any>([]);
  const [selectedWorkspaceModal, setSelectedWorkspaceModal] = useState<any>(false);
  const [updateBillingCustomer, setUpdateBillingCustomer] = useState<any>(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState<any>(user?.account?.workspaces[0].environments[0]);
  const [selectedWorkspace, setSelectedWorkspace] = useLocalStorage<any>("selectedWorkspace", [user?.account?.workspaces[0]]);
  const [mktplaceToken, setMktplaceToken] = useLocalStorage<any>("mktplaceToken", "");
  const [isAmazonAccount, setIsAmazonAccount] = useLocalStorage<any>("isAmazonAccount", false);

  const setUpdateBilling = useCallback(async (selected: any) => { setUpdateBillingCustomer(selected); }, [setSelectedAccount]);
  const setAccountCallback = useCallback(async (selected: any) => { setAccount(selected); }, [setAccount]);
  const setSelectedEnvironmentCallback = useCallback(async (selected: any) => { setSelectedEnvironment(selected); }, [setSelectedEnvironment]);

  const updateNav = (href: string) => {
    const newNav = nav.map((item: any) => {
      if (item.href === href) { return { ...item, current: true } }
      return { ...item, current: false };
    });
    setNav(newNav);
  };
  const redirectToURL = (page: string) => {
    history.push(`/${page}`);
    updateNav(page);
  };
  const handleLogout = () => {
    auth.logout();
    history.push("/");
  };
  const setNavHighlight = (pageName: string) => {
    const newNav = nav.map((item: any) => {
      if (item.name === pageName) return { ...item, current: true }
      return { ...item, current: false };
    });
    setNav(newNav);
  }
  const getUrlParameter = (name: any, history: any) => {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp(`[\\?&]${name}=([^&#]*)`);
    const results = regex.exec(history.location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  };

  const syncAccountData = async () => {
    if (auth.user && auth.user.tokens) {
      const response = await fetch(`${config.url}/account/get-account/${auth.user.attributes.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth.user.tokens.AccessToken}` },
      });
      if (response.ok && response.status === 200 && response.body !== null) {
        const data = await response.json();
        setAccount(data.account);
        setSelectedAccount(data.account);
        setSelectedWorkspace(selectedWorkspace.SK ? selectedWorkspace : data?.account?.workspaces[0]);
        setEnvironments(selectedWorkspace.environments ? selectedWorkspace.environments : data?.account?.workspaces[0]?.environments.sort().reverse());
        setSelectedEnvironmentCallback(selectedEnvironment ? selectedEnvironment : data.account.workspaces[0].environments[0]);
      }
    }
  };

  useEffect(() => { 
    const mktplaceToken = getUrlParameter('x-amzn-marketplace-token', history);
    const canceled = qs.parse(history.location.search, { ignoreQueryPrefix: true }).canceled ? true : false;
    if (canceled) { setUpdateBilling(true); history.replace({ search: "" }); } 
    if (mktplaceToken.length > 0) { 
      setMktplaceToken(mktplaceToken);
      setIsAmazonAccount(true);
    }    
  }, [ auth.user?.account, history.location.search, mktplaceToken ]);

  useEffect(() => { 
    if (!user) { handleLogout(); }
    const currentPath = location.pathname.substring(1);
    if (currentPath) redirectToURL(currentPath);
    setNav(nav.map((item: any) => { 
      if (item.href === `/${currentPath}`) return { ...item, current: true } 
      return { ...item, current: false }; 
    }));
  }, []); 

  useEffect(() => {
    if (auth.user && auth.user.account) {
      setAccountCallback(auth.user.account);
      setSelectedAccount(auth.user.account);
      setSelectedWorkspace(selectedWorkspace.length > 1 ? selectedWorkspace : auth.user.account.workspaces[0]);
      setEnvironments(selectedWorkspace?.environments ? selectedWorkspace?.environments : auth.user.account.workspaces[0].environments.sort().reverse()); 
      setSelectedEnvironmentCallback(selectedEnvironment ? selectedEnvironment : auth.user.account.workspaces[0].environments[0]);
    }
    if (auth.user && auth.user.tokens && !auth.user.account) syncAccountData().catch(console.error);
  }, [auth.user]);

  type optionsResponse = { response: any };
  let options: any;
  if (user?.account) {
    options = {
      headers: { Authorization: `Bearer ${user.tokens.AccessToken}` },
      cacheLife: 100,
      interceptors: {
        response: ({ response }: optionsResponse) => {
          if (response.status === 401) { handleLogout(); }
          return response;
        },
      },
    };
  }
  return (
  <> 
    { (user && user.account) ?
        <div className="flex flex-col animate-color-change-2x h-screen">
          <ChatwootWidget />
        <Provider url={config.url} options={options}>
          <Navigation
            user={user}
            account={account}
            selectedWorkspace={selectedWorkspace}
            setSelectedWorkspaceModal={setSelectedWorkspaceModal}
            logout={() => handleLogout()}
            nav={nav}
            history={history}
            updateNav={updateNav}
            redirectToURL={(url: string) => redirectToURL(url) }
          />
          <WorkspaceSelectModal 
            user={user}            
            selectedWorkspace={selectedWorkspace}
            setSelectedWorkspaceHandler={setSelectedWorkspace}
            closeSelectModal={() => setSelectedWorkspaceModal(false)} 
            open={selectedWorkspaceModal}
            logout={() => handleLogout()}
          />
          <Content
            account={account}
            setAccount={setAccountCallback}
            environments={environments}
            history={history}
            updateBillingCustomer={updateBillingCustomer}
            selectedEnvironment={selectedEnvironment}
            setupEnvironmentsHandler={setEnvironments}
            setSelectedEnvironment={setSelectedEnvironmentCallback}
            selectedAccount={selectedAccount}
            selectedWorkspace={selectedWorkspace}
            setSelectedWorkspace={setSelectedWorkspace}
            setSelectedAccount={setSelectedAccount}
            logout={() => handleLogout()} 
            redirectToURL={(url: string) => redirectToURL(url) }
          />
        </Provider>
      </div>
      : <Product user={user} isAmazonAccount={isAmazonAccount} mktplaceToken={mktplaceToken} setNavHighlight={setNavHighlight} />
    } 
  </>
  );
}
